import { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Video, Pencil, Loader2, Image as ImageIcon, Play, MessageSquareQuote, PenLine, FolderOpen, Trash2, Download } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { Link } from "react-router-dom";

const PHOTO_CATEGORIES = [
  "Heizraum","Heizraum – Übersicht","Heizraum – Zugang/Weg","Heizraum – Boden/Wand",
  "Aktuelle Heizung","Aktuelle Heizung – Typenschild","Aktuelle Heizung – Gesamtanlage","Aktuelle Heizung – Abgasrohr","Aktuelle Heizung – Schornstein",
  "Speicher","Speicher – Speicher","Speicher – Anschlüsse",
  "Wohnraum","Wohnraum – Heizkreisverteiler","Wohnraum – Heizkörper",
  "Zählerschrank","Zählerschrank – Geschlossen","Zählerschrank – Offen","Zählerschrank – Etikett Innentür","Zählerschrank – Hauptverteilung",
  "Leitungsweg","Leitungsweg – Innen","Leitungsweg – Außen",
  "Transportweg","Transportweg – Übersicht","Transportweg – Engstellen",
  "Außeneinheit","Außeneinheit – Aufstellort","Außeneinheit – Umgebung","Außeneinheit – Nachbarabstand",
  "Hausansicht","Hausansicht – Vorderansicht","Hausansicht – Hinteransicht","Hausansicht – Kellereingang","Hausansicht – Hauseinfahrt","Hausansicht – Fassade Zustand",
  "Sonstiges",
];
const VIDEO_CATEGORIES = ["Leitungsweg","Transportweg","Heizraum","Außenbereich","Zählerschrank","Sonstiges"];

const SecureMedia = ({ path, contentType, alt, className }) => {
  const isVideo = contentType?.startsWith("video/");
  const url = fileUrl(path);
  if (isVideo) {
    return (
      <video src={url} controls preload="metadata" className={className} />
    );
  }
  return <img src={url} alt={alt} className={className} />;
};

export default function MediaSection({ project, photos, videos, sketches, onUpdate }) {
  const [tab, setTab] = useState("photos");
  // Persistent active category — set once at the top, used for every Foto/Video
  // upload until the user changes it. iPhone-Safari-safe direct upload pattern
  // (no setTimeout-click trick, no confirm panel) replaces the previous
  // multi-step flow that confused users into thinking the upload had finished
  // when in fact they still needed to tap "Hochladen".
  const [photoCategory, setPhotoCategory] = useState("Heizraum");
  const [videoCategory, setVideoCategory] = useState("Leitungsweg");
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  // Iteration 83: eigener Ref für den Galerie-Picker (ohne `capture=environment`),
  // damit iOS die Fotomediathek/Dateien-App öffnet statt zwangsweise der Kamera.
  const galleryInputRef = useRef(null);

  const doUpload = async (file, type, category) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("category", category);
      fd.append("description", "");
      const endpoint = type === "video" ? "videos" : "photos";
      await api.post(`/projects/${project.id}/${endpoint}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${type === "video" ? "Video" : "Foto"} hochgeladen · ${category}`);
      onUpdate?.();
    } catch (er) {
      // Surface the real error so the user can act on it instead of silent failure
      const detail = er.response?.data?.detail || er.message || "unbekannter Fehler";
      toast.error(`Upload fehlgeschlagen: ${detail}`);
      console.error("[MediaSection] upload error", er);
    } finally {
      setUploading(false);
    }
  };

  // Triggered by the native file picker (label-bound input). iOS Safari opens
  // the picker because the tap target is a <label htmlFor> linked to a hidden
  // <input type="file">. The picker is part of the trusted user-gesture chain.
  const handleFilePicked = (type) => async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file later
    if (!file) return;
    const cat = type === "video" ? videoCategory : photoCategory;
    await doUpload(file, type, cat);
  };

  // Iter89a (2026-02-20) — Media löschen. Endpoint-Mapping:
  //   photo   → DELETE /projects/{id}/photos/{itemId}
  //   video   → DELETE /projects/{id}/videos/{itemId}
  //   sketch  → DELETE /projects/{id}/sketches/{itemId}
  // Nach dem Löschen `onUpdate` triggern, damit die Galerie neu lädt.
  const [deletingId, setDeletingId] = useState(null);
  const deleteItem = async (type, item) => {
    const label = type === "video" ? "Video" : type === "sketch" ? "Skizze" : "Foto";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${label} wirklich löschen?\n\nKategorie: ${item.category || "—"}\nDiese Aktion kann nicht rückgängig gemacht werden.`)) return;
    setDeletingId(item.id);
    try {
      const endpoint = type === "video" ? "videos" : type === "sketch" ? "sketches" : "photos";
      await api.delete(`/projects/${project.id}/${endpoint}/${item.id}`);
      toast.success(`${label} gelöscht`);
      onUpdate?.();
    } catch (er) {
      const detail = er.response?.data?.detail || er.message || "unbekannter Fehler";
      toast.error(`Löschen fehlgeschlagen: ${detail}`);
    } finally {
      setDeletingId(null);
    }
  };

  // Iter89z-dl (2026-02-26) — Download-Button für Fotos / Videos / Skizzen.
  // Lädt die Datei als Blob (auth via `?auth=` Query-String) und triggert
  // einen echten Browser-Download mit sprechendem Dateinamen aus Kategorie
  // + ID + Original-Dateiendung.
  const [downloadingId, setDownloadingId] = useState(null);
  const downloadItem = async (type, item) => {
    const label = type === "video" ? "Video" : type === "sketch" ? "Skizze" : "Foto";
    setDownloadingId(item.id);
    try {
      const url = fileUrl(item.storage_path);
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      // Dateiendung aus Content-Type ableiten (jpeg/png/mp4/pdf …)
      const ct = (item.content_type || blob.type || "").toLowerCase();
      let ext = "";
      if (ct.includes("jpeg") || ct.includes("jpg")) ext = ".jpg";
      else if (ct.includes("png")) ext = ".png";
      else if (ct.includes("webp")) ext = ".webp";
      else if (ct.includes("heic")) ext = ".heic";
      else if (ct.includes("mp4")) ext = ".mp4";
      else if (ct.includes("quicktime") || ct.includes("mov")) ext = ".mov";
      else if (ct.includes("pdf")) ext = ".pdf";
      else {
        // Fallback: letzte Extension aus storage_path
        const m = (item.storage_path || "").match(/\.([a-zA-Z0-9]{2,5})$/);
        ext = m ? "." + m[1].toLowerCase() : "";
      }
      const safeCat = (item.category || label).replace(/[^a-zA-Z0-9äöüÄÖÜß_-]+/g, "_");
      const fname = `${safeCat}_${(item.id || "").slice(0, 8)}${ext}`;
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      toast.success(`${label} wird heruntergeladen`);
    } catch (er) {
      const detail = er.message || "unbekannter Fehler";
      toast.error(`Download fehlgeschlagen: ${detail}`);
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="space-y-5">
      {/* Persistent category selector — set once, used for every subsequent
          Foto/Video upload. Two separate selectors for photo vs. video so the
          user can switch quickly. */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-4 grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[12px] font-semibold text-zinc-300 mb-1 uppercase tracking-widest">Foto-Kategorie</label>
          <select
            className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-sm text-zinc-50 outline-none"
            value={photoCategory}
            onChange={(e) => setPhotoCategory(e.target.value)}
            data-testid="media-photo-category-select"
          >
            {PHOTO_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[12px] font-semibold text-zinc-300 mb-1 uppercase tracking-widest">Video-Kategorie</label>
          <select
            className="h-10 w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 text-sm text-zinc-50 outline-none"
            value={videoCategory}
            onChange={(e) => setVideoCategory(e.target.value)}
            data-testid="media-video-category-select"
          >
            {VIDEO_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        {uploading && (
          <div className="col-span-2 text-xs text-[#00E575] inline-flex items-center gap-2">
            <Loader2 className="animate-spin" size={12} /> Lade hoch …
          </div>
        )}
      </div>

      {/* Action buttons – each is a <label> wrapping a hidden file input. The
          tap on the label IS the user-gesture that opens the native picker on
          iOS Safari — there is no JS-only `.click()` chain that could fail. */}
      {/* Vier Aktionskacheln — auf Mobile 2×2, auf Desktop nebeneinander.
          Foto = Kamera direkt (capture=environment) für schnelle Vor-Ort-Aufnahme.
          Galerie = File-Picker ohne capture → iOS öffnet Mediathek / Finder,
          Desktop öffnet Dateidialog. Klassischer JPEG/PNG/HEIC-Upload aus
          bestehenden Bildern. Iteration 83 (2026-02-16). */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label
          data-testid="media-take-photo-btn"
          htmlFor="media-photo-file-input"
          className={`bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 rounded-2xl p-5 transition active:scale-95 flex flex-col items-center gap-2 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
        >
          <div className="h-12 w-12 rounded-2xl bg-[#00E575]/15 flex items-center justify-center"><Camera className="text-[#00E575]" /></div>
          <span className="text-sm font-medium">Foto (Kamera)</span>
          <span className="text-[12px] text-zinc-300 truncate max-w-full" title={photoCategory}>{photoCategory}</span>
        </label>
        <input
          id="media-photo-file-input"
          ref={photoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handleFilePicked("photo")}
          className="hidden"
          data-testid="media-photo-file-input"
        />

        <label
          data-testid="media-gallery-upload-btn"
          htmlFor="media-gallery-file-input"
          className={`bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 rounded-2xl p-5 transition active:scale-95 flex flex-col items-center gap-2 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
        >
          <div className="h-12 w-12 rounded-2xl bg-[#00E575]/15 flex items-center justify-center"><FolderOpen className="text-[#00E575]" /></div>
          <span className="text-sm font-medium">Aus Galerie</span>
          <span className="text-[12px] text-zinc-300 truncate max-w-full" title={photoCategory}>{photoCategory}</span>
        </label>
        <input
          id="media-gallery-file-input"
          ref={galleryInputRef}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp,image/gif,image/heic,image/heif,image/*"
          onChange={handleFilePicked("photo")}
          className="hidden"
          data-testid="media-gallery-file-input"
        />

        <label
          data-testid="media-take-video-btn"
          htmlFor="media-video-file-input"
          className={`bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 rounded-2xl p-5 transition active:scale-95 flex flex-col items-center gap-2 cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
        >
          <div className="h-12 w-12 rounded-2xl bg-[#00E575]/15 flex items-center justify-center"><Video className="text-[#00E575]" /></div>
          <span className="text-sm font-medium">Video</span>
          <span className="text-[12px] text-zinc-300 truncate max-w-full" title={videoCategory}>{videoCategory}</span>
        </label>
        <input
          id="media-video-file-input"
          ref={videoInputRef}
          type="file"
          accept="video/*"
          capture="environment"
          onChange={handleFilePicked("video")}
          className="hidden"
          data-testid="media-video-file-input"
        />

        <Link
          to={`/projects/${project.id}/sketch`}
          data-testid="media-sketch-btn"
          className="bg-[#18181B] border border-zinc-800 hover:border-[#00E575]/50 rounded-2xl p-5 transition active:scale-95 flex flex-col items-center gap-2"
        >
          <div className="h-12 w-12 rounded-2xl bg-[#00E575]/15 flex items-center justify-center"><Pencil className="text-[#00E575]" /></div>
          <span className="text-sm font-medium">Skizze</span>
        </Link>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800">
        {[
          { id: "photos", label: `Fotos (${photos.length})`, icon: ImageIcon },
          { id: "videos", label: `Videos (${videos.length})`, icon: Play },
          { id: "sketches", label: `Skizzen (${sketches.length})`, icon: Pencil },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`media-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition ${
              tab === t.id ? "border-[#00E575] text-[#00E575]" : "border-transparent text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Gallery */}
      {tab === "photos" && (
        photos.length === 0 ? <div className="text-center py-12 text-zinc-400 text-base">Noch keine Fotos. Über die <b className="text-[#00E575]">Foto-Klingeln</b> im Tab „Technik" können Sie pro Thema direkt Bilder aufnehmen.</div> :
        (() => {
          // group photos by top-level category (before " – ")
          const groups = {};
          photos.forEach((p) => {
            const main = (p.category || "Sonstiges").split(" – ")[0];
            if (!groups[main]) groups[main] = [];
            groups[main].push(p);
          });
          return (
            <div className="space-y-5">
              {Object.entries(groups).map(([main, items]) => (
                <div key={main}>
                  <h3 className="font-display text-base font-bold text-white mb-2 flex items-center gap-2">
                    <span className="inline-block h-2 w-2 rounded-full bg-[#00E575]" /> {main} <span className="text-zinc-500 text-sm font-normal">({items.length})</span>
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {items.map((p) => {
                      const subCat = (p.category || "").includes(" – ")
                        ? p.category.split(" – ")[1]
                        : null;
                      const noteIsDefault =
                        !p.description || p.description === p.category || p.description === subCat;
                      return (
                        <div key={p.id} data-testid={`photo-${p.id}`} className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden flex flex-col">
                          <div className="aspect-square bg-zinc-950 relative group">
                            <SecureMedia path={p.storage_path} contentType={p.content_type} alt={p.description} className="w-full h-full object-cover" />
                            {!noteIsDefault && (
                              <div className="absolute top-2 right-2 h-7 w-7 rounded-full bg-[#00E575] text-[#09090B] flex items-center justify-center shadow-lg" title="Sprachnotiz vorhanden">
                                <MessageSquareQuote size={14} />
                              </div>
                            )}
                            {/* Delete overlay – top-left. Iter89a. */}
                            <button
                              type="button"
                              data-testid={`photo-delete-${p.id}`}
                              onClick={() => deleteItem("photo", p)}
                              disabled={deletingId === p.id}
                              title="Foto löschen"
                              className="absolute top-2 left-2 h-8 w-8 rounded-full bg-red-500/95 hover:bg-red-500 text-white flex items-center justify-center shadow-lg active:scale-90 transition disabled:opacity-60"
                            >
                              {deletingId === p.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            </button>
                            {/* Download overlay – top-right (unterhalb Note-Chip). Iter89z-dl. */}
                            <button
                              type="button"
                              data-testid={`photo-download-${p.id}`}
                              onClick={() => downloadItem("photo", p)}
                              disabled={downloadingId === p.id}
                              title="Foto herunterladen"
                              className={`absolute h-8 w-8 rounded-full bg-sky-500/95 hover:bg-sky-500 text-white flex items-center justify-center shadow-lg active:scale-90 transition disabled:opacity-60 ${noteIsDefault ? "top-2 right-2" : "top-11 right-2"}`}
                            >
                              {downloadingId === p.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                            </button>
                            {/* Markieren / Annotate – overlay button bottom-left */}
                            <Link
                              to={`/projects/${project.id}/photos/${p.id}/annotate`}
                              data-testid={`photo-annotate-${p.id}`}
                              title="Auf diesem Foto markieren"
                              className="absolute bottom-2 left-2 h-9 px-3 rounded-full bg-[#00E575] text-[#09090B] text-[12px] font-bold inline-flex items-center gap-1.5 shadow-lg hover:bg-[#00C964] active:scale-95 transition"
                            >
                              <PenLine size={14} /> Markieren
                            </Link>
                          </div>
                          <div className="p-3 flex-1 flex flex-col gap-1.5">
                            <div className="text-[12px] font-mono uppercase tracking-wider text-[#00E575] font-bold truncate">{p.category}</div>
                            {!noteIsDefault ? (
                              <div className="flex items-start gap-1.5 bg-[#00E575]/8 border border-[#00E575]/25 rounded-lg px-2 py-1.5 mt-0.5">
                                <MessageSquareQuote size={13} className="text-[#00E575] flex-shrink-0 mt-0.5" />
                                <p
                                  data-testid={`photo-note-${p.id}`}
                                  className="text-[12.5px] text-zinc-100 leading-snug"
                                  style={{
                                    display: "-webkit-box",
                                    WebkitLineClamp: 4,
                                    WebkitBoxOrient: "vertical",
                                    overflow: "hidden",
                                  }}
                                >
                                  {p.description}
                                </p>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          );
        })()
      )}

      {tab === "videos" && (
        videos.length === 0 ? <div className="text-center py-12 text-zinc-500">Noch keine Videos.</div> :
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {videos.map((v) => (
            <div key={v.id} data-testid={`video-${v.id}`} className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
              <div className="aspect-video bg-zinc-950 relative">
                <SecureMedia path={v.storage_path} contentType={v.content_type} className="w-full h-full" />
                <button
                  type="button"
                  data-testid={`video-delete-${v.id}`}
                  onClick={() => deleteItem("video", v)}
                  disabled={deletingId === v.id}
                  title="Video löschen"
                  className="absolute top-2 left-2 h-8 w-8 rounded-full bg-red-500/95 hover:bg-red-500 text-white flex items-center justify-center shadow-lg active:scale-90 transition disabled:opacity-60"
                >
                  {deletingId === v.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
                <button
                  type="button"
                  data-testid={`video-download-${v.id}`}
                  onClick={() => downloadItem("video", v)}
                  disabled={downloadingId === v.id}
                  title="Video herunterladen"
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-sky-500/95 hover:bg-sky-500 text-white flex items-center justify-center shadow-lg active:scale-90 transition disabled:opacity-60"
                >
                  {downloadingId === v.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                </button>
              </div>
              <div className="p-3">
                <div className="text-xs font-mono uppercase tracking-wider text-[#00E575]">{v.category}</div>
                {v.description && <div className="text-sm text-zinc-300 truncate mt-0.5">{v.description}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "sketches" && (
        sketches.length === 0 ? <div className="text-center py-12 text-zinc-500">Noch keine Skizzen.</div> :
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {sketches.map((s) => (
            <div key={s.id} data-testid={`sketch-${s.id}`} className="bg-[#18181B] border border-zinc-800 rounded-2xl overflow-hidden">
              <div className="aspect-square bg-zinc-950 flex items-center justify-center relative">
                <SecureMedia path={s.storage_path} contentType={s.content_type} alt={s.title} className="w-full h-full object-contain" />
                <button
                  type="button"
                  data-testid={`sketch-delete-${s.id}`}
                  onClick={() => deleteItem("sketch", s)}
                  disabled={deletingId === s.id}
                  title="Skizze löschen"
                  className="absolute top-2 left-2 h-8 w-8 rounded-full bg-red-500/95 hover:bg-red-500 text-white flex items-center justify-center shadow-lg active:scale-90 transition disabled:opacity-60"
                >
                  {deletingId === s.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                </button>
                <button
                  type="button"
                  data-testid={`sketch-download-${s.id}`}
                  onClick={() => downloadItem("sketch", s)}
                  disabled={downloadingId === s.id}
                  title="Skizze herunterladen"
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-sky-500/95 hover:bg-sky-500 text-white flex items-center justify-center shadow-lg active:scale-90 transition disabled:opacity-60"
                >
                  {downloadingId === s.id ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
                </button>
              </div>
              <div className="p-3">
                <div className="text-xs font-mono uppercase tracking-wider text-[#00E575]">{s.category}</div>
                <div className="text-sm text-zinc-300 truncate mt-0.5">{s.title || "Skizze"}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
